import { useConfirmModal } from '@/components/ConfirmModalProvider';
import { useModal } from '@/components/ModalProvider';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import {
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import config from '@/config';
import {
  ApiKey,
  AuditLogByEntityQuery,
  AuditLogInsertInput,
  DeviceByPkQuery,
} from '@/generated/graphql';
import { execute } from '@/graphql/execute';
import { AuditLogAction, AuditLogEntityType } from '@/models/audit-log.models';
import { FeatureName } from '@/models/role.models';
import { createAuditLogInput } from '@/utils/audit-log.function';
import hasPermission from '@/utils/permission.function';
import {
  QueryObserverResult,
  RefetchOptions,
  useMutation,
} from '@tanstack/react-query';
import { Copy, Plus, X } from 'lucide-react';
import { useRef } from 'react';
import { toast } from 'sonner';
import {
  createDeviceApiKeysMutation,
  deleteApiKeyMutation,
} from '../../graphql/mutation';

interface DeviceApiKeysTabProps {
  deviceId: string;
  apiKeys: ApiKey[];
  refetchDevice: (
    options?: RefetchOptions,
  ) => Promise<QueryObserverResult<DeviceByPkQuery, Error>>;
  refetchAuditLog: (
    options?: RefetchOptions,
  ) => Promise<QueryObserverResult<AuditLogByEntityQuery, Error>>;
}

export function DeviceApiKeysTab({
  deviceId,
  apiKeys,
  refetchDevice,
  refetchAuditLog,
}: DeviceApiKeysTabProps) {
  const { openModal } = useConfirmModal();
  const modal = useModal();
  const apiKeyDescriptionRef = useRef<HTMLInputElement>(null);
  const apiKeyDescriptionEnabled = config.apiKey.hasDescription;

  const { mutateAsync: createDeviceApiKeys, isPending } = useMutation({
    mutationKey: ['CreateDeviceApiKeysMutation'],
    mutationFn: (options: {
      input: {
        createDeviceApiKeys: { deviceId: string; description?: string }[];
      };
      auditLogInput: AuditLogInsertInput;
    }) => {
      return execute({
        query: createDeviceApiKeysMutation,
        variables: {
          ...options,
        },
      });
    },
    onSuccess: async () => {
      await refetchDevice();
      await refetchAuditLog();
      toast.success('API key created successfully!', {
        duration: 3000,
      });
    },
  });

  const { mutateAsync: deleteApiKey } = useMutation({
    mutationKey: ['DeleteApiKey'],
    mutationFn: ({
      id,
      auditLogInput,
    }: {
      id: string;
      auditLogInput: AuditLogInsertInput;
    }) => {
      return execute({
        query: deleteApiKeyMutation,
        variables: {
          id,
          auditLogInput,
        },
      });
    },
    onSuccess: async () => {
      await refetchDevice();
      await refetchAuditLog();
      toast.success('API key removed successfully!', {
        duration: 3000,
      });
    },
  });

  const confirmDeleteApiKey = (id: string) => {
    const apiKey = apiKeys.find((t) => t.id === id);
    const displayKey = apiKey?.reference
      ? `${apiKey.reference}...`
      : apiKey?.key;
    openModal(
      {
        message: (
          <div className="grid gap-2">
            <p>Are you sure you want to delete this item?</p>
            {displayKey && (
              <div className="border-border bg-muted/30 rounded-md border px-3 py-2 text-sm">
                {apiKeyDescriptionEnabled && apiKey?.description && (
                  <div className="text-muted-foreground">
                    {apiKey.description}
                  </div>
                )}
                <div className="font-mono break-all">{displayKey}</div>
              </div>
            )}
          </div>
        ),
      },
      async () => {
        await deleteKey(id);
      },
    );
  };

  const deleteKey = async (id: string) => {
    await deleteApiKey({
      id,
      auditLogInput: createAuditLogInput({
        action: AuditLogAction.DELETE_DEVICE_API_KEY,
        entities: [
          {
            entity: AuditLogEntityType.DEVICE,
            id: deviceId,
          },
          {
            entity: AuditLogEntityType.API_KEY,
            id: id,
          },
        ],
      }),
    });
  };

  const addApiKey = async (description?: string) => {
    const trimmedDescription = apiKeyDescriptionEnabled
      ? description?.trim()
      : undefined;
    const result = await createDeviceApiKeys({
      input: {
        createDeviceApiKeys: [
          {
            deviceId,
            description: trimmedDescription || undefined,
          },
        ],
      },
      auditLogInput: createAuditLogInput({
        action: AuditLogAction.CREATE_DEVICE_API_KEY,
        entities: [
          {
            id: deviceId,
            entity: AuditLogEntityType.DEVICE,
          },
        ],
      }),
    });

    const apiKey = result?.createDeviceApiKeys?.apiKeys[0]?.key;
    if (!apiKey) {
      toast.error('Failed to retrieve the new API key');
      return;
    }

    openApiKeyModal(apiKey);
  };

  const handleCopyApiKey = async (apiKey: string) => {
    try {
      await navigator.clipboard.writeText(apiKey);
      toast.success('API key copied to clipboard');
    } catch (error) {
      console.error('Failed to copy API key', error);
      toast.error('Failed to copy API key');
    }
  };

  const openApiKeyModal = (apiKey: string) => {
    modal.openModal(
      <>
        <DialogHeader>
          <DialogTitle>API Key</DialogTitle>
        </DialogHeader>
        <DialogContent>
          <div className="mx-1 mb-6 flex items-center justify-center gap-2 font-mono font-semibold">
            <span className="break-all">{apiKey}</span>
            <Button
              className="ms-4"
              type="button"
              variant={'outline'}
              size={'icon'}
              onClick={() => {
                void handleCopyApiKey(apiKey);
              }}
              aria-label="Copy API key"
            >
              <Copy className="h-4 w-4" />
            </Button>
          </div>
          <div>
            <p className="text-red font-semibold">
              This API key is only displayed once.
            </p>
            <p className="text-muted-foreground text-sm">
              Make sure you copy it now.
            </p>
          </div>
        </DialogContent>
        <DialogFooter>
          <Button type="button" variant={'outline'} onClick={modal.closeModal}>
            Close
          </Button>
        </DialogFooter>
      </>,
    );
  };

  const openAddApiKeyModal = () => {
    openModal(
      {
        title: 'Create API key',
        message: (
          <div className="grid gap-3">
            {apiKeyDescriptionEnabled ? (
              <div className="grid gap-2">
                <Label htmlFor="apiKeyDescription">
                  Description{' '}
                  <span className="text-muted-foreground">(optional)</span>
                </Label>
                <Input
                  id="apiKeyDescription"
                  ref={apiKeyDescriptionRef}
                  placeholder="e.g. Field unit 12"
                  autoFocus
                />
                <p className="text-muted-foreground text-sm">
                  Add a label to help identify this key later.
                </p>
              </div>
            ) : (
              <p className="text-muted-foreground text-sm">
                Description is disabled for this environment.
              </p>
            )}
          </div>
        ),
        confirmText: 'Create',
        cancelText: 'Cancel',
      },
      async () => {
        await addApiKey(apiKeyDescriptionRef.current?.value);
      },
    );
  };
  return (
    <Card className="relative">
      <CardHeader>
        <CardTitle>API key</CardTitle>
        {hasPermission(FeatureName.CREATE_UPDATE_API_KEYS) && (
          <Button
            variant={'outline'}
            size={'sm'}
            className="absolute top-4 right-4"
            onClick={openAddApiKeyModal}
            disabled={isPending}
          >
            <Plus /> Add key
          </Button>
        )}
      </CardHeader>
      <CardContent>
        <div className="grid gap-2">
          <div className="flex justify-between"></div>
          {apiKeys.length > 0 ? (
            apiKeys.map((t) => {
              return (
                <div className="grid gap-1" key={t.id}>
                  {apiKeyDescriptionEnabled && t.description && (
                    <div>
                      <span className="text-muted-foreground text-sm">
                        {t.description}
                      </span>
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    <div className="flex-1">
                      <Input
                        type="text"
                        readOnly
                        id={`apiKey-${t.id}`}
                        placeholder="API key"
                        defaultValue={t.reference ? t.reference + '...' : t.key}
                      />
                    </div>
                    {hasPermission(FeatureName.CREATE_UPDATE_API_KEYS) && (
                      <Button
                        variant={'outline'}
                        size={'icon'}
                        onClick={() => confirmDeleteApiKey(t.id as string)}
                      >
                        <X />
                      </Button>
                    )}
                  </div>
                </div>
              );
            })
          ) : (
            <div className="text-muted-foreground text-sm font-medium">
              No API key
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
